import React, { useEffect, useState } from 'react';
import { Lock, KeyRound, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const STORAGE_KEY = 'ces-buyer-access-unlocked';
const CORRECT_PIN = '1707';
interface PinGateProps {
  children: React.ReactNode;
}
export function PinGate({
  children
}: PinGateProps) {
  const [unlocked, setUnlocked] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  // Read sessionStorage on mount to avoid flicker during SSR-style hydration
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
        setUnlocked(true);
      }
    } catch (e) {
      // ignore — sessionStorage may be unavailable
    }
    setHydrated(true);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setUnlocked(true);
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch (e) {
        // ignore
      }
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
    }
  };
  if (!hydrated) {
    // While we check storage, render nothing to prevent flash of locked screen
    return null;
  }
  return <>
      {unlocked && children}

      <AnimatePresence data-id="element-176" data-mp="6wnk2plcpbs1">
        {!unlocked && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-background p-4" data-id="element-177" data-mp="6uuap2jcsdgc">
            <motion.div initial={{
          y: 12,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.35,
          delay: 0.05
        }} className="w-full max-w-md" animate-shake={shake ? 'true' : undefined} data-id="element-178" data-mp="2hnnf34all31u">
              <motion.div animate={shake ? {
            x: [0, -10, 10, -10, 10, 0]
          } : {
            x: 0
          }} transition={{
            duration: 0.4
          }} className="overflow-hidden rounded-2xl border bg-card shadow-xl" data-id="element-179" data-mp="egfzb168g8er">
                {/* Header */}
                <div className="border-b bg-muted/40 px-8 py-6 text-center" data-id="element-180" data-mp="3rpazs2fmo9m9">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary" data-id="element-181" data-mp="1uu68ung4fovs">
                    <Lock className="h-7 w-7" data-id="element-182" data-mp="1ptsajx4bntay" />
                  </div>
                  <h1 className="mt-4 text-xl font-bold text-foreground" data-id="element-183" data-mp="1xekwp6z9zkx1">
                    Buyer Access Required
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground" data-id="element-184" data-mp="274xkr96atw7x">
                    This portal contains confidential operational information.
                    Please enter your access PIN to continue.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-8 py-6" data-id="element-185" data-mp="1eimh1pnebf6w">
                  <label htmlFor="pin-input" className="block text-sm font-medium text-foreground mb-2" data-id="element-186" data-mp="1jfv0ruo6zra9">
                    Access PIN
                  </label>
                  <div className="relative" data-id="element-187" data-mp="1tbfyhgm8a5e7">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" data-id="element-188" data-mp="2zue6jnzeyev6" />
                    <input id="pin-input" type="password" inputMode="numeric" autoComplete="off" autoFocus value={pin} onChange={e => {
                  setPin(e.target.value);
                  if (error) setError(false);
                }} placeholder="Enter PIN" className={`h-11 w-full rounded-md border bg-background pl-10 pr-3 text-base tracking-widest outline-none transition-all ${error ? 'border-destructive focus:ring-2 focus:ring-destructive/40' : 'focus:border-primary focus:ring-2 focus:ring-primary/30'}`} data-id="element-189" data-mp="34r4nsemyfgi6" />
                  </div>
                  {error && <p className="mt-2 text-sm text-destructive" data-id="element-190" data-mp="3q5olf2f7922m">
                      Incorrect PIN. Please try again.
                    </p>}

                  <button type="submit" className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50" disabled={!pin} data-id="element-191" data-mp="3xopx4tgbpnp">
                    Unlock Portal
                  </button>

                  <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-muted-foreground" data-id="element-192" data-mp="ihj297db23wz">
                    <ShieldCheck className="h-3.5 w-3.5" data-id="element-193" data-mp="3swq1eiz5s9z" />
                    Private &amp; Confidential — Buyer access only
                  </div>
                </form>
              </motion.div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}